import { z } from "zod";

/**
 * Length limits for a workflow's name and description.
 *
 * These were declared in `apps/backend/src/workflow/utils/workflow-limits.ts`,
 * which `packages/types` cannot import — so a contract needing the same bounds
 * had no way to reach them and would have written 3 / 100 / 5000 out a fourth
 * time. They live here now and the backend module re-exports them, so every
 * existing reader keeps its import path and there is exactly one definition.
 *
 * The readers, and why each one needs the numbers rather than a schema:
 * `UpdateWorkflowDetailsBodySchema` (the multipart PUT body), the hand-written
 * `@ApiBody` on that route — which cannot be derived from a schema because it
 * also documents a binary `file` field — `WorkflowMutationService`'s own
 * guards, and `ZWorkflows.UpdateDetails`.
 */
export const WORKFLOW_NAME_MIN_LENGTH = 3;
export const WORKFLOW_NAME_MAX_LENGTH = 100;
export const WORKFLOW_DESCRIPTION_MAX_LENGTH = 5000;

/**
 * The workflow details form — name and description — as ONE schema.
 *
 * Every door onto those two fields derives from this: the multipart
 * `PUT /api/workflow/:id/details` body is this schema, and the JSON
 * `PATCH /api/workflow/:id/details` body is this schema picked and made
 * partial. Restating either field rule beside this one would be a second source
 * of truth that agrees today and drifts on the first change to either bound.
 *
 * `.trim()` comes BEFORE `.min()` deliberately, so `"  a  "` is measured as one
 * character rather than five and is refused. `WorkflowMutationService` trims
 * before its own guard for the same reason; the two agree because both measure
 * the trimmed value.
 */
export const workflowDetailsSchemaBase = z.object({
  name: z.string().trim().min(WORKFLOW_NAME_MIN_LENGTH).max(WORKFLOW_NAME_MAX_LENGTH),
  description: z.string().max(WORKFLOW_DESCRIPTION_MAX_LENGTH).optional()
});

export type WorkflowDetails = z.infer<typeof workflowDetailsSchemaBase>;
